import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import { getMe, AuthUser } from "@/lib/Auth/authApi";
import { useAuthStore } from "@/lib/Auth/authStore";

export const meQueryKey = ["auth", "me"] as const;

export function meQueryOptions(token: string | null) {
  return {
    queryKey: meQueryKey,
    queryFn: async () => {
      if (!token) return null;
      return await getMe(token);
    },
    staleTime: 5 * 60 * 1000,
  };
}

/**
 * Nullable variant for guest/error contexts that must render without a user
 * (sign-in/onboarding chrome, the error boundary). Non-suspense, never
 * redirects.
 */
export function useOptionalUser(): AuthUser | null {
  const { token } = useAuthStore();
  const { data } = useQuery(meQueryOptions(token));
  return data?.user ?? null;
}

/**
 * Guaranteed user for authed screens: suspends until /me resolves and
 * redirects to /sign-in when there is no user. Use `useOptionalUser` on screens
 * that can render signed-out.
 */
export function useUser(): AuthUser {
  const { token } = useAuthStore();
  const { data } = useSuspenseQuery(meQueryOptions(token));

  if (!data?.user) {
    throw redirect({ to: "/sign-in" });
  }

  return data.user;
}
